import { fromApiAccountingTag } from "../../../configs/accountingTags.configs";
import type {
  ComptaMouvementApiResource,
  ComptaMouvementAffectationApiResource,
  ComptaMouvementAffectationPayload,
  LedgerAllocationCategorizationRuleApiResource,
  SepaRemittanceApiResource,
  SepaRemittanceIssueApi,
  SettlementApiResource,
} from "../../../types/accounting-api.types";
import type {
  DeepSearchMatch,
  DeepSearchMatchType,
  LedgerAllocationCategorizationRule,
  SepaRemittance,
  SepaRemittanceIssue,
  SepaRemittanceProcess,
  SepaRemittanceStatus,
  Settlement,
} from "../../../types/accounting.types";
import type {
  BankMovement, BankMovementDuplicateCandidate, FusionReason, LedgerAllocation, LedgerAllocationWritePayload,
} from "../../models/admin";

const toNullableNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const BRIDGE_SOURCE_KEYS = [
  "bridge",
  "bridge_data",
  "bridge_id",
  "bridge_uuid",
  "bridge_connection_id",
  "bridge_transaction_id",
  "bridge_item_id",
  "bridge_provider",
  "bridge_source",
  "bridge_api",
] as const;

const detectMovementSource = (resource: ComptaMouvementApiResource): "Bridge" | "Saisie" => {
  const directSource = typeof resource.source === "string" ? resource.source.trim() : "";
  if (directSource) {
    return directSource.toLowerCase().includes("bridge") ? "Bridge" : "Saisie";
  }

  const bridgeProvider = typeof resource.bridge_provider === "string" ? resource.bridge_provider.trim() : "";
  if (bridgeProvider) return "Bridge";

  const bridgeSource = typeof resource.bridge_source === "string" ? resource.bridge_source.trim() : "";
  if (bridgeSource) return "Bridge";

  const bridgeValue = resource.bridge;
  if (typeof bridgeValue === "string" && bridgeValue.trim()) {
    return "Bridge";
  }

  if (bridgeValue && typeof bridgeValue === "object") {
    const provider = "provider" in bridgeValue && typeof bridgeValue.provider === "string"
      ? bridgeValue.provider.trim()
      : "";
    if (provider) return "Bridge";
    return "Bridge";
  }

  const hasBridgeData = BRIDGE_SOURCE_KEYS.some((key) => {
    const value = resource[key];
    if (typeof value === "string") return value.trim().length > 0;
    return value !== null && value !== undefined;
  });

  return hasBridgeData ? "Bridge" : "Saisie";
};

const VALID_MATCH_TYPES = new Set<string>(["label", "allocation", "operation", "operation_type", "client"]);

const isMatchType = (raw: string | undefined): raw is DeepSearchMatchType =>
  raw !== undefined && VALID_MATCH_TYPES.has(raw);

const toMatchType = (raw: string | undefined): DeepSearchMatchType =>
  isMatchType(raw) ? raw : "allocation";

type RawMatch = {
  type?: string
  label?: string
  field?: string
  value?: string
};

const mapDeepSearchMatches = (
  rawMatches: Array<RawMatch> | null | undefined,
): DeepSearchMatch[] | null => {
  if (!Array.isArray(rawMatches) || rawMatches.length === 0) return null;
  return rawMatches.map((m) => ({
    type: toMatchType(m.type),
    label: m.label ?? undefined,
    field: m.field ?? undefined,
    value: m.value ?? undefined,
  }));
};

export const fromApiDuplicateCandidate = (resource: {
  bank_movement: ComptaMouvementApiResource
  original: ComptaMouvementApiResource
  created_at?: string | null
  original_created_at?: string | null
  pass_repeats?: number | null
  pass_size?: number | null
  allocations_count?: number | null
}): BankMovementDuplicateCandidate => ({
  bankMovement: fromApiBankMovement(resource.bank_movement),
  original: fromApiBankMovement(resource.original),
  createdAt: resource.created_at ?? null,
  originalCreatedAt: resource.original_created_at ?? null,
  passRepeats: Number(resource.pass_repeats ?? 0),
  passSize: Number(resource.pass_size ?? 0),
  allocationsCount: Number(resource.allocations_count ?? 0),
});

export const fromApiBankMovement = (resource: ComptaMouvementApiResource): BankMovement => ({
  id: String(resource.id),
  tags: (resource.tags ?? []).map(fromApiAccountingTag),
  date: resource.date ?? null,
  depositDate: resource.deposit_date ?? resource.date_de_depot ?? null,
  issueDate: resource.issue_date ?? resource.date_emission ?? null,
  createdAt: resource.created_at ?? resource.createdAt ?? null,
  label: resource.label ?? resource.libelle ?? "",
  accountId: resource.bank_account?.id ? String(resource.bank_account.id) : null,
  accountName: resource.bank_account?.name ?? resource.compta_entreprise_compte?.nom ?? null,
  accountDemo: resource.bank_account?.demo ?? false,
  paymentMode: resource.payment_method ?? resource.mode_reglement ?? null,
  paymentNumber: resource.payment_number ?? resource.numero_paiement ?? null,
  remittanceNumber: resource.remittance_number ?? resource.remise_numero ?? null,
  dateFusion: resource.date_fusion ?? resource.dateFusion ?? null,
  source: detectMovementSource(resource),
  bridgeBankDescription: (resource.bank_description ?? resource.bridge_bank_description ?? null),
  bridgeCleanDescription: (resource.bank_clean_description ?? resource.bridge_clean_description ?? null),
  comptaPiece: resource.compta_piece?.url
    ? {
      url: resource.compta_piece.url,
      signedId: resource.compta_piece.signed_id ?? null,
    }
    : null,
  isPiece: Boolean(resource.is_piece),
  fileType: resource.file_type ?? null,
  fileName: resource.file_name ?? null,
  debit: toNullableNumber(resource.debit),
  credit: toNullableNumber(resource.credit),
  bankDebit: toNullableNumber(resource.bank_debit),
  bankCredit: toNullableNumber(resource.bank_credit),
  allocationBalance: toNullableNumber(resource.remaining_amount ?? resource.solde_affectation),
  ledgerAllocationsCount: toNullableNumber(resource.ledger_allocations_count),
  operationTypes: (resource.operation_types ?? []).map((operationType) => ({
    id: String(operationType.id),
    name: operationType.name ?? "",
  })),
  uncategorizedAllocationsCount: resource.uncategorized_allocations_count ?? 0,
  locked: Boolean(resource.locked),
  bankDiscrepancy: resource.bank_discrepancy
    ? {
      amount: toNullableNumber(resource.bank_discrepancy.amount) ?? 0,
      acknowledged: Boolean(resource.bank_discrepancy.acknowledged),
      acknowledgedAt: resource.bank_discrepancy.acknowledged_at ?? null,
      remainingAfterAccept: toNullableNumber(resource.bank_discrepancy.remaining_after_accept) ?? 0,
      bankCredit: toNullableNumber(resource.bank_discrepancy.bank_credit),
      bankDebit: toNullableNumber(resource.bank_discrepancy.bank_debit),
    }
    : null,
  fusionScore: toNullableNumber(resource.fusion_score),
  fusionReasons: (resource.fusion_reasons ?? []) as FusionReason[],
  fusionProbable: resource.fusion_probable ?? null,
  probableFusion: resource.probable_fusion
    ? {
      status: (resource.probable_fusion.status ?? "none") as "none" | "proposed" | "declined" | "accepted",
      score: toNullableNumber(resource.probable_fusion.score),
      updatedAt: resource.probable_fusion.updated_at ?? null,
      candidate: resource.probable_fusion.candidate?.id
        ? {
          id: String(resource.probable_fusion.candidate.id),
          rawId: resource.probable_fusion.candidate.raw_id !== null
            && resource.probable_fusion.candidate.raw_id !== undefined
            ? String(resource.probable_fusion.candidate.raw_id)
            : null,
          label: resource.probable_fusion.candidate.label ?? "",
          date: resource.probable_fusion.candidate.date ?? null,
          debit: toNullableNumber(resource.probable_fusion.candidate.debit),
          credit: toNullableNumber(resource.probable_fusion.candidate.credit),
          bridgeBankDescription: resource.probable_fusion.candidate.bridge_bank_description ?? null,
          bridgeCleanDescription: resource.probable_fusion.candidate.bridge_clean_description ?? null,
          paymentNumber: resource.probable_fusion.candidate.payment_number ?? null,
        }
        : null,
    }
    : null,
  deepSearchMatches: mapDeepSearchMatches(resource.deep_search_matches),
  clientIbanInfo: resource.client_iban_info?.consistent
    ? {
      clientId: resource.client_iban_info.client_id!,
      clientName: resource.client_iban_info.client_name ?? "",
      iban: resource.client_iban_info.iban ?? null,
      bic: resource.client_iban_info.bic ?? null,
      bicCalcule: resource.client_iban_info.bic_calcule ?? null,
      ibanVerified: resource.client_iban_info.iban_verified ?? false,
      ibanReason: resource.client_iban_info.iban_reason ?? null,
      consistent: true,
    }
    : null,
  brStatus: resource.br_status ?? null,
  brStatusReason: resource.br_status_reason ?? null,
  brDate: resource.br_date ?? null,
  rawId: resource.raw_id !== null && resource.raw_id !== undefined ? String(resource.raw_id) : null,
  brPaymentTransactionId: resource.br_payment_transaction_id ?? null,
});

export const fromApiLedgerAllocation = (
  resource: ComptaMouvementAffectationApiResource,
): LedgerAllocation => ({
  tags: (resource.tags ?? []).map(fromApiAccountingTag),
  sepaReturns: (resource.sepa_returns ?? []).map((entry) => ({
    amount: Number(entry.amount), date: entry.date, returnMovementId: entry.return_movement_id, retryRemittanceIds: entry.retry_remittance_ids,
  })),
  draftExpiresAt: resource.draft_expires_at ?? null,
  id: String(resource.id),
  bankMovementId: resource.bank_movement_id
    ? String(resource.bank_movement_id)
    : (resource.compta_mouvement_id ? String(resource.compta_mouvement_id) : null),
  bankMovementLabel: resource.bank_movement?.label ?? null,
  orderId: resource.order_id ? String(resource.order_id) : null,
  activityOperationId: resource.activity_operation_id ? String(resource.activity_operation_id) : null,
  date: resource.date ?? null,
  budgetType: resource.type_budget ?? null,
  operationType: resource.type_movement ?? resource.type_operation ?? null,
  operationTypeId: resource.operation_type_id ? String(resource.operation_type_id) : null,
  operationTypeName:
    resource.operation_type?.name
    ?? resource.compta_type_operation?.nom
    ?? resource.compta_type_operation?.name
    ?? resource.compta_type_operations?.nom
    ?? resource.compta_type_operations?.name
    ?? null,
  description: resource.label ?? resource.autre ?? null,
  otherDescription: resource.autre ?? null,
  locked: Boolean(resource.locked),
  debit: toNullableNumber(resource.debit),
  credit: toNullableNumber(resource.credit),
  stockReference: resource.reference_stock ?? null,
  stockName: resource.nom_stock ?? null,
  stockFamily: resource.famille_stock ?? null,
  stockActivity: resource.activite_stock ?? null,
  stockDebitQuantity: toNullableNumber(resource.nombre_unite_stock_debit),
  stockCreditQuantity: toNullableNumber(resource.nombre_unite_stock_credit),
  stockUnitValue: toNullableNumber(resource.stock_unit_value),
  stockDebitAmount: toNullableNumber(resource.stock_debit_amount),
  stockCreditAmount: toNullableNumber(resource.stock_credit_amount),
  stockExpiresOn: resource.stock_expires_on ?? null,
  stockReferenceId: resource.stock_reference_id ? String(resource.stock_reference_id) : null,
  supplierId: resource.supplier_id ? String(resource.supplier_id) : null,
  stockReferenceName: resource.stock_reference?.name ?? null,
  stockReferenceStockValue: resource.stock_reference?.stock_value ? toNullableNumber(resource.stock_reference.stock_value) : null,
  supplierName: resource.supplier?.name ?? null,
  activityOperationCategory: resource.activity_operation?.category ?? null,
  activityOperationDebit: toNullableNumber(resource.activity_operation?.debit),
  activityOperationCredit: toNullableNumber(resource.activity_operation?.credit),
  activityOperationClientName: resource.activity_operation?.client_name ?? null,
  activityOperationClientSite: resource.activity_operation?.client_site ?? null,
  invoiceId: resource.activity_operation?.invoice?.id ?? null,
  invoiceKind: resource.activity_operation?.invoice?.kind ?? null,
  invoiceLabel: resource.activity_operation?.invoice?.label ?? null,
  // ADM-603 : 3 supports possibles pour un justificatif (active op,
  // remboursement, mouvement bancaire). Le trombone du Grand Livre
  // s'allume dès qu'un seul est présent — sinon les PJ uploadés
  // directement depuis le drawer du mouvement bancaire restent
  // invisibles dans la liste.
  hasAttachments:
    (resource.activity_operation?.attachments?.some((att) => Boolean(att.url)) ?? false)
    || (resource.activity_operation?.refund?.attachments?.some((att) => Boolean(att.url)) ?? false)
    || Boolean(resource.bank_movement?.compta_piece?.url),
  bankMovementAttachment: resource.bank_movement?.compta_piece?.url
    ? {
      url: resource.bank_movement.compta_piece.url,
      fileName: resource.bank_movement.compta_piece.file_name ?? "",
      contentType: resource.bank_movement.compta_piece.content_type ?? "",
    }
    : null,
  activityOperationAttachments: (resource.activity_operation?.attachments ?? []).map((att) => ({
    label: att.label ?? "",
    url: att.url ?? "",
    fileName: att.file_name ?? "",
    contentType: att.content_type ?? "",
  })),
  beneficiaryName: resource.activity_operation?.beneficiary_name ?? null,
  activityOperationLabel: resource.activity_operation?.label ?? null,
  activityOperationStatus: resource.activity_operation?.status ?? null,
  refundId: resource.activity_operation?.refund?.id ? String(resource.activity_operation.refund.id) : null,
  refundStatusName: resource.activity_operation?.refund?.status_name ?? null,
  refundAmountRetained: toNullableNumber(resource.activity_operation?.refund?.amount_retained),
  refundAmountPaid: toNullableNumber(resource.activity_operation?.refund?.amount_paid),
  refundAllocationBalance: toNullableNumber(resource.activity_operation?.refund?.allocation_balance),
  refundCreatedAt: resource.activity_operation?.refund?.created_at ?? null,
  refundIban: resource.activity_operation?.refund?.iban ?? null,
  refundBic: resource.activity_operation?.refund?.bic ?? null,
  refundClientName: resource.activity_operation?.refund?.client_name ?? null,
  refundClientSite: resource.activity_operation?.refund?.client_site ?? null,
  refundReferenceName: resource.activity_operation?.refund?.reference_name ?? null,
  refundReferenceId: resource.activity_operation?.refund?.reference_id ? String(resource.activity_operation.refund.reference_id) : null,
  refundAttachments: (resource.activity_operation?.refund?.attachments ?? [])
    .filter((a) => a.url)
    .map((a) => ({
      label: a.label ?? "",
      url: a.url ?? "",
      fileName: a.file_name ?? "",
      contentType: a.content_type ?? "",
    })),
  refundBeneficiaries: (resource.activity_operation?.refund?.beneficiaries ?? []).map((b) => ({
    id: b.id ? String(b.id) : "",
    name: b.name ?? "",
    holderType: b.holder_type ?? null,
    invoiceAmount: toNullableNumber(b.invoice_amount),
    invoiceUnitAmount: toNullableNumber(b.invoice_unit_amount),
    unitsRequested: toNullableNumber(b.units_requested),
    amountRetained: toNullableNumber(b.amount_retained),
  })),
  refundOperations: (resource.activity_operation?.refund?.operations ?? []).map((op) => ({
    id: op.id ? String(op.id) : "",
    label: op.label ?? "",
    status: op.status ?? "",
    beneficiary: op.beneficiary ?? "",
    holderType: op.holder_type ?? null,
    debit: toNullableNumber(op.debit),
    credit: toNullableNumber(op.credit),
    allocationBalance: toNullableNumber(op.allocation_balance),
    allocations: (op.allocations ?? []).map((a) => ({
      id: a.id ? String(a.id) : "",
      date: a.date ?? null,
      label: a.label ?? "",
      debit: toNullableNumber(a.debit),
      credit: toNullableNumber(a.credit),
      typeBudget: a.type_budget ?? null,
      bankMovementId: a.bank_movement_id ? String(a.bank_movement_id) : null,
    })),
  })),
  // E-commerce order fields
  ecommerceOrderId: resource.activity_operation?.ecommerce_order?.id ? String(resource.activity_operation.ecommerce_order.id) : null,
  ecommerceOrderRef: resource.activity_operation?.ecommerce_order?.ref ?? null,
  ecommerceOrderTotal: toNullableNumber(resource.activity_operation?.ecommerce_order?.total),
  ecommerceOrderSubsidyUsed: toNullableNumber(resource.activity_operation?.ecommerce_order?.subsidy_used),
  ecommerceOrderTotalAfterSubsidy: toNullableNumber(resource.activity_operation?.ecommerce_order?.total_after_subsidy),
  ecommerceOrderAllocationBalance: toNullableNumber(resource.activity_operation?.ecommerce_order?.allocation_balance),
  ecommerceOrderCreatedAt: resource.activity_operation?.ecommerce_order?.created_at ?? null,
  ecommerceOrderStatusName: resource.activity_operation?.ecommerce_order?.status_name ?? null,
  ecommerceOrderStatusCode: resource.activity_operation?.ecommerce_order?.status_code ?? null,
  ecommerceOrderClientName: resource.activity_operation?.ecommerce_order?.client_name ?? null,
  ecommerceOrderClientSite: resource.activity_operation?.ecommerce_order?.client_site ?? null,
  ecommerceOrderOperations: (resource.activity_operation?.ecommerce_order?.operations ?? []).map((op) => ({
    id: op.id ? String(op.id) : "",
    label: op.label ?? "",
    status: op.status ?? "",
    beneficiary: op.beneficiary ?? "",
    category: op.category ?? null,
    debit: toNullableNumber(op.debit),
    credit: toNullableNumber(op.credit),
    allocationBalance: toNullableNumber(op.allocation_balance),
    stockDebitQuantity: toNullableNumber(op.nombre_unite_stock_debit),
    stockCreditQuantity: toNullableNumber(op.nombre_unite_stock_credit),
    stockBalanceQuantity: toNullableNumber(op.solde_affectation_stock_nombre),
    familyName: op.family_name ?? null,
    referenceName: op.reference_name ?? null,
    referenceId: op.reference_id ? String(op.reference_id) : null,
    allocations: (op.allocations ?? []).map((a) => ({
      id: a.id ? String(a.id) : "",
      date: a.date ?? null,
      label: a.label ?? "",
      debit: toNullableNumber(a.debit),
      credit: toNullableNumber(a.credit),
      typeBudget: a.type_budget ?? null,
      stockDebitUnits: toNullableNumber(a.nombre_unite_stock_debit),
      stockCreditUnits: toNullableNumber(a.nombre_unite_stock_credit),
      stockDebitAmount: toNullableNumber(a.debit_stock),
      stockCreditAmount: toNullableNumber(a.credit_stock),
      bankMovementId: a.bank_movement_id ? String(a.bank_movement_id) : null,
    })),
  })),
  // Catalog booking fields (locations) — mirror du bloc ecommerce_order
  catalogBookingId: resource.activity_operation?.catalog_booking?.id ?? null,
  catalogBookingReference: resource.activity_operation?.catalog_booking?.reference ?? null,
  catalogBookingStatus: resource.activity_operation?.catalog_booking?.status ?? null,
  catalogBookingPaymentStatus: resource.activity_operation?.catalog_booking?.payment_status ?? null,
  catalogBookingTotalCents: toNullableNumber(resource.activity_operation?.catalog_booking?.total_price_cents),
  catalogBookingSubsidyCents: toNullableNumber(resource.activity_operation?.catalog_booking?.subsidy_amount_cents),
  catalogBookingRemainingCents: toNullableNumber(resource.activity_operation?.catalog_booking?.remaining_amount_cents),
  catalogBookingStartDate: resource.activity_operation?.catalog_booking?.start_date ?? null,
  catalogBookingEndDate: resource.activity_operation?.catalog_booking?.end_date ?? null,
  catalogBookingCreatedAt: resource.activity_operation?.catalog_booking?.created_at ?? null,
  catalogBookingItemName: resource.activity_operation?.catalog_booking?.item_name ?? null,
  catalogBookingItemId: resource.activity_operation?.catalog_booking?.item_id ?? null,
  catalogBookingClientName: resource.activity_operation?.catalog_booking?.client_name ?? null,
  catalogBookingClientSite: resource.activity_operation?.catalog_booking?.client_site ?? null,
  // Settlement fields
  settlementId: resource.settlement?.id ? String(resource.settlement.id) : null,
  settlementNumber: resource.settlement?.number ?? null,
  settlementDate: resource.settlement?.date ?? null,
  settlementIssuer: resource.settlement?.issuer ?? null,
  settlementPaymentMethod: resource.settlement?.payment_method ?? null,
  settlementAmount: toNullableNumber(resource.settlement?.amount),
  settlementRemainingAmount: toNullableNumber(resource.settlement?.remaining_amount),
  settlementDeferredDate: resource.settlement?.deferred_date ?? null,
  resolvedBankAccountId: resource.resolved_bank_account?.id
    ? String(resource.resolved_bank_account.id)
    : (
      resource.bank_movement?.bank_account?.id
        ? String(resource.bank_movement.bank_account.id)
        : (resource.settlement?.bank_account?.id ? String(resource.settlement.bank_account.id) : null)
    ),
  resolvedBankAccountName: resource.resolved_bank_account?.name
    ?? resource.bank_movement?.bank_account?.name
    ?? resource.settlement?.bank_account?.name
    ?? null,
  resolvedBankAccountDemo: resource.resolved_bank_account?.demo ?? false,
  deepSearchMatches: mapDeepSearchMatches(resource.deep_search_matches),
});

export const fromApiSettlement = (raw: SettlementApiResource): Settlement => ({
  id: String(raw.id),
  date: raw.date ?? null,
  year: raw.year !== null ? String(raw.year) : null,
  number: raw.number ?? null,
  issuer: raw.issuer ?? null,
  paymentMethod: raw.payment_method ?? null,
  amount: raw.amount !== null ? Number(raw.amount) : null,
  remainingAmount: raw.remaining_amount !== null ? Number(raw.remaining_amount) : null,
  deferredDate: raw.deferred_date ?? null,
  clientName: raw.client_name ?? null,
  bankAccount: raw.bank_account
    ? { id: String(raw.bank_account.id), name: raw.bank_account.name ?? null }
    : null,
  bankMovementId: raw.bank_movement_id !== null ? String(raw.bank_movement_id) : null,
  bankMovementDate: raw.bank_movement_date ?? null,
  deposited: raw.deposited ?? false,
  createdAt: raw.created_at ?? null,
  updatedAt: raw.updated_at ?? null,
  deepSearchMatches: mapDeepSearchMatches(raw.deep_search_matches),
});

export const fromApiCategorizationRule = (
  rule: LedgerAllocationCategorizationRuleApiResource,
): LedgerAllocationCategorizationRule => {
  const budgetType = String(rule.budget_type ?? "ASC").toUpperCase();
  return {
    id: String(rule.id),
    name: rule.name ?? "",
    budgetType: budgetType === "AEP" ? "AEP" : "ASC",
    operationTypeId: rule.operation_type_id !== null && rule.operation_type_id !== undefined
      ? String(rule.operation_type_id)
      : null,
    operationTypeName: rule.operation_type_name ?? null,
    active: rule.active ?? true,
    conditions: Array.isArray(rule.conditions) ? rule.conditions : [],
    position: typeof rule.position === "number" ? rule.position : 0,
    createdAt: rule.created_at ?? null,
    updatedAt: rule.updated_at ?? null,
  };
};

export const toApiLedgerAllocationPayload = (
  payload: LedgerAllocationWritePayload,
): ComptaMouvementAffectationPayload => ({
  ledger_allocation: {
    ...(payload.temporary !== undefined ? { temporary: payload.temporary } : {}),
    bank_movement_id: payload.bankMovementId,
    ledger_account_id:
      payload.companyAccountId && !Number.isNaN(Number(payload.companyAccountId))
        ? Number(payload.companyAccountId)
        : null,
    label: payload.description ?? null,
    date: payload.date ?? null,
    type_budget: payload.budgetType ?? null,
    type_movement: payload.operationType ?? null,
    operation_type_id: payload.operationTypeId ? Number(payload.operationTypeId) : null,
    debit: payload.debit ?? null,
    credit: payload.credit ?? null,
    activite_operation_id: payload.activityOperationId ?? null,
    ...(payload.settlementId !== undefined ? { settlement_id: payload.settlementId } : {}),
    stock_reference_id: payload.stockReferenceId ? Number(payload.stockReferenceId) : null,
    supplier_id: payload.supplierId || null,
    stock_debit_quantity: payload.stockDebitQuantity ?? null,
    stock_credit_quantity: payload.stockCreditQuantity ?? null,
    ...(payload.stockExpiresOn !== undefined ? { stock_expires_on: payload.stockExpiresOn } : {}),
  },
});

// ── SEPA remittances (YTI-1732) ──────────────────────────────

const issueValueToString = (value: SepaRemittanceIssueApi["expected"] | SepaRemittanceIssueApi["actual"]): string | null => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.map((v) => (v === null ? "—" : String(v))).join(", ");
  return String(value);
};

const fromApiSepaRemittanceIssue = (raw: SepaRemittanceIssueApi): SepaRemittanceIssue => ({
  code: raw.code,
  details: raw.details,
  message: raw.message,
  expected: issueValueToString(raw.expected),
  actual: issueValueToString(raw.actual),
  ids: raw.ids ?? [],
  missingIds: raw.missing_ids ?? [],
  extraIds: raw.extra_ids ?? [],
});

const toSepaProcess = (value: string): SepaRemittanceProcess =>
  value === "remiseScindee" ? "remiseScindee" : "regroupement";

const toSepaStatus = (value: string): SepaRemittanceStatus =>
  value === "rolled_back" ? "rolled_back" : "issued";

export const fromApiSepaRemittance = (raw: SepaRemittanceApiResource): SepaRemittance => ({
  id: String(raw.id),
  rawId: raw.raw_id,
  msgId: raw.msg_id,
  paymentLabel: raw.payment_label ?? null,
  process: toSepaProcess(raw.process),
  status: toSepaStatus(raw.status),
  executionDate: raw.execution_date ?? null,
  transactionCount: raw.transaction_count,
  totalAmount: toNullableNumber(raw.total_amount) ?? 0,
  issuedAt: raw.issued_at,
  representativeId: raw.representative_id ?? null,
  representativeName: raw.representative_name ?? null,
  bankAccountId: raw.bank_account_id ?? null,
  userGroup: raw.user_group ?? null,
  bankWorkflow: raw.bank_workflow ?? null,
  remittanceMovementId: raw.remittance_movement_id ?? null,
  movementIds: raw.movement_ids ?? [],
  navigationMovements: raw.navigation_movements?.map((m) => ({ ...m, amount: toNullableNumber(m.amount) })),
  beneficiaries: (raw.beneficiaries ?? []).map((b) => ({
    clientId: b.client_id,
    endToEndId: b.end_to_end_id ?? null,
    label: b.remittance_information ?? null,
    name: b.name,
    iban: b.iban ?? null,
    amount: toNullableNumber(b.amount) ?? 0,
    allocationIds: b.allocation_ids ?? [],
  })),
  banked: raw.banked,
  deposited: raw.deposited ?? false,
  originAvailable: raw.origin_available ?? false,
  historyComplete: raw.history_complete ?? false,
  canRegenerate: raw.can_regenerate ?? false,
  canRollback: raw.can_rollback ?? false,
  events: (raw.events ?? []).map((e) => ({
    id: e.id, kind: e.kind, occurredAt: e.occurred_at, recordedAt: e.recorded_at,
    byName: e.by_name ?? null, byId: e.by_id ?? null, version: e.version ?? null, details: e.details,
  })),
  rolledBackAt: raw.rolled_back_at ?? null,
  rolledBackById: raw.rolled_back_by_id ?? null,
  xmlUrl: raw.xml_url ?? null,
  csvUrl: raw.csv_url ?? null,
  followUp: raw.follow_up
    ? {
      currentAmount: toNullableNumber(raw.follow_up.current_amount) ?? 0,
      reassignedPayments: raw.follow_up.reassigned_payments.map((p) => ({
        name: p.name, amount: toNullableNumber(p.amount) ?? 0, remittanceId: p.remittance_id, msgId: p.msg_id,
      })),
    }
    : undefined,
  versionsCount: raw.versions_count ?? (raw.versions?.length ?? 0),
  integrity: raw.integrity
    ? {
      inSync: raw.integrity.in_sync,
      issues: (raw.integrity.issues ?? []).map(fromApiSepaRemittanceIssue),
      checkedAt: raw.integrity.checked_at ?? null,
    }
    : null,
  // Les versions sont présentées de la plus récente à la plus ancienne.
  versions: (raw.versions ?? []).map((v, index) => ({
    number: v.number ?? index + 1,
    available: v.available ?? false,
    csvAvailable: v.csv_available ?? false,
    legacy: v.legacy ?? true,
    snapshot: v.snapshot ?? null,
    at: v.at,
    byId: v.by_id ?? null,
    byName: v.by_name ?? null,
    reason: v.reason,
    transactionCount: v.transaction_count ?? null,
    totalAmount: toNullableNumber(v.total_amount),
    xmlSha256: v.xml_sha256 ?? null,
  })).sort((a, b) => b.number - a.number),
  movements: (raw.movements ?? []).map((m) => ({
    id: m.id,
    prefixId: m.prefix_id,
    label: m.libelle ?? null,
    debit: toNullableNumber(m.debit) ?? 0,
    credit: toNullableNumber(m.credit) ?? 0,
    paymentMode: m.mode_reglement ?? null,
    paymentNumber: m.numero_paiement ?? null,
    depositDate: m.date_de_depot ?? null,
    allocationIds: m.allocation_ids ?? [],
  })),
});
